import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { money } from "@/components/finance/financeUtils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock3, Loader2, XCircle } from "lucide-react";

const TERMINAL_FAILURES = new Set(["verification_mismatch", "verification_failed", "failed"]);
const PROCESSING_STATUSES = new Set(["pending", "initialized", "verified"]);

export const classifyCombinedConfirmation = (data) => {
  if (!data || typeof data.status !== "string") return "unknown";
  if (data.status === "verified" && ["completed", "partial"].includes(data.allocation_status)) return "success";
  if (TERMINAL_FAILURES.has(data.status)) return "failure";
  if (PROCESSING_STATUSES.has(data.status)) return "processing";
  return "unknown";
};

const PaymentConfirmation = () => {
  const [state, setState] = useState("checking");
  const [receipt, setReceipt] = useState(null);
  const [failureCode, setFailureCode] = useState(null);
  const [pending] = useState(() => { try { return JSON.parse(sessionStorage.getItem("dfa_paystack_pending") || "null"); } catch { return null; } });
  useEffect(() => {
    if (!pending || !pending.reference) { setState("unknown"); return; }
    let active = true; let attempts = 0; let timer;
    const check = async () => {
      try {
        if (pending.combined) {
          const { data } = await api.get(`/finance/gateway/paystack/receipts/${pending.reference}`);
          if (!active) return;
          const outcome = classifyCombinedConfirmation(data);
          if (outcome === "success") {
            setReceipt(data); sessionStorage.removeItem("dfa_paystack_pending"); setState("success"); return;
          }
          if (outcome === "failure") {
            setFailureCode(data.status); sessionStorage.removeItem("dfa_paystack_pending"); setState("failure"); return;
          }
          if (outcome === "unknown") {
            setState("unknown"); return;
          }
        } else {
          const { data } = await api.get(`/finance/placements/${pending.student_id}/detail`, { params: { enrollment_id: pending.enrollment_id, program_id: pending.program_id, session_id: pending.session_id, term_id: pending.term_id } });
          if (!active) return;
          const invoice = Array.isArray(data?.invoices) && data.invoices.find((item) => item.id === pending.invoice_id);
          if (invoice && Number(invoice.net_balance_minor || 0) === 0) { sessionStorage.removeItem("dfa_paystack_pending"); setState("success"); return; }
        }
      } catch (error) {
        if (!active) return;
        if (error?.response?.status === 404) { setState("unknown"); return; }
        /* Browser return is never payment proof. Transient errors remain bounded. */
      }
      attempts += 1; if (active && attempts < 20) timer = setTimeout(check, 3000); else if (active) setState("pending");
    };
    check(); return () => { active = false; clearTimeout(timer); };
  }, [pending]);
  const childUrl = pending?.student_id ? `/parent/child/${pending.student_id}/fees` : "/parent";
  const title = state === "success" ? "Payment Successful" : state === "checking" ? "Confirming payment…" : state === "failure" ? "Payment could not be confirmed" : state === "unknown" ? "Payment status unavailable" : "Payment confirmation pending";
  const message = state === "success"
    ? "DFA has verified and applied the payment. Your canonical accounts have been refreshed."
    : state === "failure" && failureCode === "verification_mismatch"
      ? "The amount confirmed by the payment gateway did not match the DFA invoice amount. No payment has been applied."
      : state === "failure"
        ? "DFA could not verify this payment, so no payment has been applied to the student's account."
        : state === "pending"
          ? "DFA is still confirming this payment. Please do not pay again until the school confirms the result."
          : state === "unknown"
            ? "DFA could not find a valid pending checkout in this browser. A callback address alone is not payment proof."
            : "The browser return is not payment proof. DFA will show success only after server-side verification completes.";
  return <Card className="max-w-lg mx-auto p-8 text-center">
    <div className="w-14 h-14 rounded-full bg-[#9DCD3F]/15 mx-auto grid place-items-center">{state === "success" ? <CheckCircle2 className="text-emerald-600" /> : state === "checking" ? <Loader2 className="animate-spin text-[#1B2C5A]" /> : state === "failure" ? <XCircle className="text-red-600" /> : <Clock3 className="text-amber-600" />}</div>
    <h1 className="font-display text-2xl text-[#1B2C5A] mt-4">{title}</h1>
    <p className="text-sm text-[#475569] mt-2">{message}</p>
    {state === "failure" && pending?.reference && <p className="text-xs text-[#475569] mt-3">Reference: {pending.reference}</p>}
    {receipt && <div className="mt-5 text-left text-sm border rounded-lg p-4"><div className="font-medium text-[#1B2C5A] mb-2">Combined receipt · {receipt.reference}</div>{[["Total received", receipt.total_received_minor], ["Total allocated", receipt.total_allocated_minor], ["Available credit", receipt.available_credit_minor]].map(([label, value]) => <div key={label} className="flex justify-between"><span>{label}</span><strong>{money(value)}</strong></div>)}{(receipt.settlements || []).map((item) => <div key={item.id} className="mt-3 pt-3 border-t"><div className="flex justify-between gap-3"><span>{item.metadata?.student_name || "Student"} · {item.metadata?.program_name || "Programme"}</span><strong>{money(item.amount_minor)}</strong></div><div className="text-xs text-[#475569]">Allocated {money(item.allocated_amount_minor)} · Placement credit {money(item.unallocated_amount_minor)}</div>{(item.allocations || []).map((allocation) => <div key={allocation.id} className="text-xs flex justify-between mt-1"><span>Invoice {allocation.invoice_id}</span><span>{money(allocation.amount_minor)}</span></div>)}</div>)}</div>}
    <Button asChild className="mt-5"><Link to={childUrl}>Return to Finance</Link></Button>
  </Card>;
};

export default PaymentConfirmation;
